"use client";

import React, { useEffect, useRef, useState } from "react";
import Image from "next/image";
import { Linkedin, Pause, Play } from "lucide-react";
import { PR_TESTIMONIALS, type PRTestimonial } from "./prTestimonialsData";
import styles from "./PRTestimonialsMarquee.module.css";

const LinkedInBadge = () => <Linkedin aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />;

const Avatar: React.FC<{ t: PRTestimonial; className?: string }> = ({
  t,
  className = "h-11 w-11 text-sm",
}) => {
  const [ok, setOk] = useState(Boolean(t.photo));
  return (
    <div
      className={`relative flex shrink-0 items-center justify-center overflow-hidden rounded-full border border-[#f0c9a3] bg-[#FFF3EB] font-semibold text-[#F97316] ${className}`}
    >
      {ok && t.photo ? (
        <Image
          src={t.photo}
          alt=""
          width={112}
          height={112}
          sizes="56px"
          className="h-full w-full object-cover"
          onError={() => setOk(false)}
        />
      ) : (
        t.initials
      )}
    </div>
  );
};

const NameRow: React.FC<{ t: PRTestimonial; dark?: boolean; duplicate?: boolean }> = ({
  t,
  dark = false,
  duplicate = false,
}) => (
  <a
    tabIndex={duplicate ? -1 : undefined}
    href={t.linkedinUrl}
    target="_blank"
    rel="noopener noreferrer"
    className="group inline-flex items-center gap-3 transition-opacity hover:opacity-80"
  >
    <Avatar t={t} />
    <span className="min-w-0">
      <span
        className={`flex items-center gap-1.5 text-sm font-semibold ${
          dark ? "text-white" : "text-[#231617]"
        }`}
      >
        {t.name}
        <LinkedInBadge />
      </span>
      <span
        className={`block text-xs ${dark ? "text-[#9a8f86]" : "text-[#7f6d70]"}`}
      >
        {t.company ? `${t.role} · ${t.company}` : t.role}
      </span>
    </span>
  </a>
);

type Tone = "light" | "dark";

const MarqueeCard: React.FC<{ t: PRTestimonial; tone: Tone; duplicate?: boolean }> = ({ t, tone, duplicate = false }) => {
  const dark = tone === "dark";
  return (
    <figure
      className={`flex w-[320px] shrink-0 flex-col rounded-2xl border p-6 md:w-[380px] ${
        dark
          ? "border-[#3a2327] bg-[#160B0C]"
          : "border-[#EDE7E1] bg-white shadow-sm"
      }`}
    >
      <blockquote
        className={`flex-1 text-sm leading-relaxed ${
          dark ? "text-[#e7ded9]" : "text-[#3C2227]"
        }`}
      >
        &ldquo;{t.quote}&rdquo;
      </blockquote>
      <figcaption className="mt-5">
        <NameRow t={t} dark={dark} duplicate={duplicate} />
      </figcaption>
    </figure>
  );
};

const MarqueeRow: React.FC<{ items: PRTestimonial[]; tone: Tone; reverse?: boolean }> = ({ items, tone, reverse }) => (
  <div className={styles.viewport}>
    <div className={styles.track} style={{ animationDirection: reverse ? "reverse" : "normal" }}>
      {[false, true].map((duplicate) => (
        <div key={String(duplicate)} aria-hidden={duplicate || undefined} className={`${styles.group} ${duplicate ? styles.duplicate : ""}`}>
          {items.map((testimonial) => (
            <MarqueeCard key={testimonial.name} t={testimonial} tone={tone} duplicate={duplicate} />
          ))}
        </div>
      ))}
    </div>
  </div>
);

export const PRTestimonialsMarquee: React.FC<{
  tone?: Tone;
  showHeading?: boolean;
}> = ({ tone = "light", showHeading = false }) => {
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);
  const [paused, setPaused] = useState(false);
  const [enhanced, setEnhanced] = useState(false);

  useEffect(() => {
    setEnhanced(true);
    let intersecting = false;
    const update = () => setVisible(intersecting && !document.hidden);
    const observer = new IntersectionObserver(([entry]) => {
      intersecting = entry.isIntersecting;
      update();
    });
    if (sectionRef.current) observer.observe(sectionRef.current);
    document.addEventListener("visibilitychange", update);
    return () => {
      observer.disconnect();
      document.removeEventListener("visibilitychange", update);
    };
  }, []);

  const half = Math.ceil(PR_TESTIMONIALS.length / 2);
  return (
    <section
      ref={sectionRef}
      aria-label="Designer testimonials"
      data-running={visible}
      data-paused={paused}
      data-enhanced={enhanced}
      className={`${styles.section} relative overflow-hidden pb-12 md:pb-16 ${tone === "dark" ? "bg-[#0F0708] text-white" : "bg-[#F8F9FB] text-[#3C2227]"}`}
    >
      {showHeading && <h2 className="mb-10 px-5 text-center font-prociono text-3xl md:text-5xl">Feedback designers act on.</h2>}
      <div className="flex flex-col gap-5">
        <MarqueeRow items={PR_TESTIMONIALS.slice(0, half)} tone={tone} />
        <MarqueeRow items={PR_TESTIMONIALS.slice(half)} tone={tone} reverse />
      </div>
      <div className={`${styles.controls} mt-5 flex justify-center`}>
        <button type="button" onClick={() => setPaused(!paused)} aria-pressed={paused}
          className="inline-flex min-h-11 items-center gap-2 rounded-lg px-3 text-xs focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#F97316]">
          {paused ? <Play className="h-3.5 w-3.5" aria-hidden="true" /> : <Pause className="h-3.5 w-3.5" aria-hidden="true" />}
          {paused ? "Resume scrolling" : "Pause scrolling"}
        </button>
      </div>
    </section>
  );
};
